import { createElement, memo, useCallback } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { MiList, MiStackedTypography } from "@miplatform/components";
import { PAGINATION } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { ORDERS_DEFAULT_FIELD_WIDTH } from "../ordersConstants";
import { OrderAlerts } from "./OrderAlerts/OrderAlerts";
import { OrderCardHomeField } from "./OrderCardHomeField/OrderCardHomeField";
import { OrderCardOrderTypeField } from "./OrderCardOrderTypeField/OrderCardOrderTypeField";
import { OrderHolidayIndicator } from "./OrderHolidayIndicator/OrderHolidayIndicator";
import { OrderInfoPopper } from "./OrderInfoPopper/OrderInfoPopper";
import { ORDERS_LIST_CONFIG } from "./orderListConfig";

const OrdersListInternal = memo(
  ({
    activeTab,
    isLoading,
    search,
    refresh,
    refreshPagination,
    orderItems,
    orderCardConfig,
    orderHoverCardConfig,
    tenantHolidays,
    setOrderItems,
    getOrderItems,
    onRefresh,
    orderReasons,
    groupedMode,
    groupedCriteria,
  }) => {
    const { classes, theme } = useStyles();
    const { navigate } = useRouter();

    const isOrderOnHoliday = useCallback(
      (approvedStartDate) => {
        if (!approvedStartDate || !tenantHolidays?.length) return null;
        const orderDate = moment(approvedStartDate);
        return (
          tenantHolidays.find((holiday) => {
            const start = moment(holiday.startDate).startOf("day");
            const end = moment(holiday.endDate).endOf("day");
            return orderDate.isBetween(start, end, undefined, "[]");
          }) || null
        );
      },
      [tenantHolidays]
    );

    const onRowClick = (order) => {
      navigate(`/orders/${order.id}`);
    };

    const sections = [
      {
        fieldsetStyle: {
          justifyContent: "flex-end",
          alignItems: "center",
        },
        fieldset: [
          {
            field: "priority",
            maxWidth: 400,
            justifyContent: "flex-end",
            renderCell: (row) => {
              return (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  gap={theme.spacing(2)}
                >
                  {orderCardConfig?.map(({ label, fieldKey }) => (
                    <MiStackedTypography
                      key={fieldKey}
                      title={label}
                      className={classes.fieldMargin}
                      titleProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
                      description={parseFieldValue(
                        getNestedValue(row, fieldKey),
                        label.toLowerCase().includes("date")
                          ? "--/--/--"
                          : undefined,
                        undefined,
                        { isCurrency: fieldKey === "variance", currency: "USD" }
                      )}
                      descriptionProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
                    />
                  ))}
                  <OrderCardOrderTypeField {...row} />
                  <OrderHolidayIndicator
                    holiday={isOrderOnHoliday(row.approvedStartDate)}
                  />
                </Box>
              );
            },
          },
        ],
      },
    ];

    const renderCardFields = ({ home }) => <OrderCardHomeField home={home} />;

    return (
      <MiList
        showGroupCount
        groupedMode={
          groupedMode || ORDERS_LIST_CONFIG[activeTab.value]?.getGroupedCriteria
        }
        viewType="card"
        onRowClick={onRowClick}
        cardProps={{
          sections: ORDERS_LIST_CONFIG[activeTab.value]?.sections || sections,
          isAlert: ORDERS_LIST_CONFIG[activeTab.value]?.isAlert,
          alertColor: ORDERS_LIST_CONFIG[activeTab.value]?.alertColor,
          isLate: ORDERS_LIST_CONFIG[activeTab.value]?.isLate,
          renderTitle: (order) => (
            <Box display="flex" alignItems="center">
              <Typography color="primary.main" fontWeight={600} fontSize={14}>
                {order.home?.address?.addressLine1 || " "}
              </Typography>
              <OrderInfoPopper
                order={order}
                fieldsConfig={orderHoverCardConfig}
              />

              {ORDERS_LIST_CONFIG[activeTab.value]?.getAlerts && (
                <OrderAlerts
                  alerts={ORDERS_LIST_CONFIG[activeTab.value]?.getAlerts(order)}
                />
              )}
            </Box>
          ),
          leftCardProps: {
            fieldset: [
              {
                field: "homeId",
                valueGetter: renderCardFields,
              },
            ],
          },
          renderItemActions: (item) =>
            ORDERS_LIST_CONFIG[activeTab.value].ActionsComponent
              ? createElement(
                  ORDERS_LIST_CONFIG[activeTab.value].ActionsComponent || null,
                  {
                    ...item,
                    order: item,
                    onRefresh,
                    orderReasons,
                    tenantHolidays,
                    getAlerts: ORDERS_LIST_CONFIG[activeTab.value].getAlerts,
                  }
                )
              : undefined,
        }}
        groupedCriteria={
          groupedCriteria ||
          ORDERS_LIST_CONFIG[activeTab.value].getGroupedCriteria
        }
        data={orderItems}
        loading={isLoading}
        refresh={refresh}
        refreshPagination={refreshPagination}
        search={search}
        paginationMode={PAGINATION.SERVER}
        setData={setOrderItems}
        dataProvider={getOrderItems}
        defaultPageSize={25}
        groupedTitle={ORDERS_LIST_CONFIG[activeTab.value].getGroupedTitle}
      />
    );
  }
);

/**
 * OrdersList component.
 * Displays a list of orderItems grouped by submission date, with detailed information, actions, and filtering options.
 *
 * This component uses memoization for optimization and prevents unnecessary re-renders using the custom comparison function `orderListCompare`.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.activeTab - The currently active tab object containing its value and label.
 * @param {boolean} props.isLoading - Indicates if the data is currently loading.
 * @param {string} props.search - The current search query.
 * @param {boolean} props.refresh - Flag to trigger data refresh.
 * @param {boolean} props.refreshPagination - Flag to trigger pagination refresh.
 * @param {Function} props.setOrderItems - Function to set the order items data.
 * @param {Function} props.getOrderItems - Function to fetch the order items data.
 * @param {Function} props.onRefresh - Callback function to be called on refresh action.
 * @param {Array<Object>} props.orderCardConfig - Configuration for fields to display on the order cards.
 * @param {Array<Object>} props.orderHoverCardConfig - Configuration for fields to display in the hover popper.
 * @param {Array<Object>} props.orderReasons - An array of order reason objects for additional context/actions.
 * @param {Array<Object>} props.orderItems - An array of order objects to display.
 * @param {boolean} [props.groupedMode] - Optional flag to enable grouped mode.
 * @param {Function} [props.groupedCriteria] - Optional function to determine grouping criteria.
 * @returns {JSX.Element} - Rendered OrdersList component.
 */
export const OrdersList = (props) => <OrdersListInternal {...props} />;

const useStyles = makeStyles()((theme) => ({
  fieldMargin: {
    marginRight: theme.spacing(2),
  },
}));
